<template>
  <v-dialog
    :model-value="modelValue"
    max-width="380"
    scrim="#000000"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="bg-primary-500" rounded="xl" elevation="24">
      <div class="d-flex align-center justify-space-between px-5 pt-4 pb-4">
        <span class="text-subtitle-1 font-weight-bold text-white">{{
          t("chatbot.feedback.modal-title")
        }}</span>
        <v-btn
          icon
          size="x-small"
          variant="text"
          color="primary-300"
          class="mr-n1 mt-n1"
          @click="close"
        >
          <v-icon size="16">mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="d-flex flex-wrap ga-2 px-5 pb-4">
        <v-chip
          v-for="tag in FEEDBACK_TAGS"
          :key="tag"
          size="small"
          :variant="selectedTags.includes(tag) ? 'flat' : 'outlined'"
          :color="selectedTags.includes(tag) ? 'tertiary-900' : 'primary-300'"
          @click="toggleTag(tag)"
        >
          {{ t(`chatbot.feedback.tags.${tag}`) }}
        </v-chip>
      </div>

      <div class="px-5 pb-4">
        <label class="feedback-field d-block rounded-lg">
          <textarea
            v-model="comment"
            :placeholder="t('chatbot.feedback.comment-placeholder')"
            rows="2"
            class="feedback-textarea"
          />
        </label>
      </div>

      <div class="d-flex justify-end px-5 pb-5">
        <v-btn
          color="tertiary-900"
          variant="flat"
          size="small"
          rounded="lg"
          :disabled="!canSubmit"
          @click="handleSubmit"
        >
          {{ t("chatbot.feedback.submit") }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const FEEDBACK_TAGS = [
  "incorrect",
  "incomplete",
  "irrelevant",
  "too-long",
  "misunderstood",
] as const;

const { t } = useI18n();

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
  submit: [comment: string | undefined];
}>();

const comment = ref("");
const selectedTags = ref<string[]>([]);

const canSubmit = computed(
  () => selectedTags.value.length > 0 || comment.value.trim().length > 0,
);

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      comment.value = "";
      selectedTags.value = [];
    }
  },
);

function toggleTag(tag: string) {
  const idx = selectedTags.value.indexOf(tag);
  if (idx === -1) selectedTags.value.push(tag);
  else selectedTags.value.splice(idx, 1);
}

function close() {
  emit("update:modelValue", false);
}

function handleSubmit() {
  const tagLabels = selectedTags.value.map((tag) =>
    t(`chatbot.feedback.tags.${tag}`),
  );
  const tagsLine = tagLabels.join(" | ");
  const commentLine = comment.value.trim();
  const parts = [tagsLine, commentLine].filter(Boolean);
  emit("submit", parts.length ? parts.join("\n") : undefined);
  close();
}
</script>

<style scoped>
.feedback-field {
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.05);
  padding: 10px 12px;
  cursor: text;
  transition: border-color 0.15s ease;
}

.feedback-field:focus-within {
  border-color: rgba(255, 255, 255, 0.4);
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.15);
}

.feedback-textarea {
  display: block;
  width: 100%;
  background: transparent;
  border: none;
  outline: none;
  resize: none;
  color: white;
  font-size: 0.875rem;
  line-height: 1.5;
  font-family: inherit;
}

.feedback-textarea::placeholder {
  color: rgba(255, 255, 255, 0.4);
}
</style>
